import {
    addDays,
    addHours,
    addMinutes,
    addMonths,
    addWeeks,
    addYears,
    format,
    getDay,
} from "date-fns";

function parseRepeatDays(raw) {
    if (!raw) return null;
    if (Array.isArray(raw)) return raw.map(Number);
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(Number) : null;
    } catch {
        return null;
    }
}

function applyTimeOfDay(date, task) {
    const timeRaw = task?.start_time ? String(task.start_time).trim().slice(0, 5) : "09:00";
    const [h, m] = timeRaw.split(":").map(Number);
    const next = new Date(date);
    next.setHours(h || 9, m || 0, 0, 0);
    return next;
}

function advanceWithWeekdays(from, days) {
    let cursor = addDays(from, 1);
    for (let i = 0; i < 14; i++) {
        if (days.includes(getDay(cursor))) return cursor;
        cursor = addDays(cursor, 1);
    }
    return addDays(from, 1);
}

function getStepFn(task) {
    const repetition = String(task?.repetition || "none").toLowerCase();
    const unit = task?.repeat_unit || null;
    const interval = Math.max(1, Number(task?.repeat_interval) || 1);
    const days = parseRepeatDays(task?.repeat_days);

    if (unit === "minute") return (d) => addMinutes(d, interval);
    if (unit === "hour") return (d) => addHours(d, interval);
    if (unit === "day") {
        if (days?.length) return (d) => advanceWithWeekdays(d, days);
        return (d) => addDays(d, interval);
    }
    if (unit === "week") return (d) => addWeeks(d, interval);
    if (unit === "month") return (d) => addMonths(d, interval);
    if (unit === "year") return (d) => addYears(d, interval);

    const presets = {
        daily: (d) => (days?.length ? advanceWithWeekdays(d, days) : addDays(d, interval)),
        weekdays: (d) => advanceWithWeekdays(d, days || [1, 2, 3, 4, 5]),
        weekly: (d) => addWeeks(d, interval),
        monthly: (d) => addMonths(d, interval),
        annually: (d) => addYears(d, interval),
        yearly: (d) => addYears(d, interval),
        custom: (d) => addDays(d, interval),
    };

    return presets[repetition] || null;
}

function isEnded(task, date) {
    const endType = task?.repeat_end_type || "never";
    if (endType === "until_date" && task?.repeat_end_date) {
        return date > new Date(task.repeat_end_date);
    }
    if (endType === "after_count" && task?.repeat_end_count != null) {
        return (task?.occurrence_index ?? 0) >= Number(task.repeat_end_count);
    }
    return false;
}

export function getNextOccurrences(task, count = 5) {
    const repetition = String(task?.repetition || "none").toLowerCase();
    const step = getStepFn(task);
    if (!step || repetition === "none") return [];

    const base = task.start_date
        ? new Date(task.start_date)
        : task.due_date
          ? new Date(task.due_date)
          : new Date();

    if (Number.isNaN(base.getTime())) return [];

    const results = [];
    let cursor = applyTimeOfDay(new Date(base), task);

    for (let i = 0; i < count; i++) {
        if (i > 0) {
            cursor = applyTimeOfDay(step(cursor), task);
        }
        if (isEnded(task, cursor)) break;
        const label = format(cursor, "EEE, d MMM yyyy");
        const timeSuffix = task.start_time ? ` at ${String(task.start_time).slice(0, 5)}` : "";
        results.push({ date: new Date(cursor), label: `${label}${timeSuffix}` });
    }

    return results;
}
